import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import api from '../../../services/api';
import { formatDate } from '../../../utils/Utils';
import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import GenericSelect from '../../../components/ui/GenericSelect';
import PanelCard from '../../performance-operations/components/PanelCard';
import { Grid, Table } from '../../performance-operations/styles';
import { usePanel, formatNumber } from '../../performance-operations/config';
import AiCenterKpis from '../components/AiCenterKpis';
import { DRAFT_KINDS, QUALITY_CODES, VERDICTS, fetchQuality, reviewStatement } from '../config';

// P7a — E15 yönetim görünümü (yalnız İK; analiz §4 "kaynaklı AI taslağının kalite ve süre göstergeleri E15 yönetim görünümüne
// eklenir", §33): M71 kaynak doğruluk, M72 taslak kullanım, M73 hazırlık süresi değişimi, M74 kaynaklı ifade kapsaması; tür
// başına üretim/doğrulama hatası/kullanım ve medyan süreler ("kullanılmayan taslaklar ve doğrulama hataları gizlenmez").
// M71 örneklemi İK incelemesiyle oluşur: inceleme kuyruğunda her doğrulanabilir ifade, dayandığı kaynaklarla birlikte "doğru /
// yanlış / doğrulanamaz" işaretlenir; karar verilince ifade metni silinir (sayı kalır).
export default function ManagementTab({ containerHeight }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [refreshKey, setRefreshKey] = useState(0);
  const quality = usePanel(() => fetchQuality(), [refreshKey]);

  const [status, setStatus] = useState('pending');
  const [kind, setKind] = useState('');
  const [page, setPage] = useState({ pageNumber: 1, pageSize: 10 });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    setFetching(true);
    api.get('/v1/performance-ai-center/quality/statements', { params: { status, ...(kind ? { kind } : {}), ...page } })
      .then((res) => {
        if (!res?.data?.status) { setRows([]); setTotal(0); return; }
        const r = res.data.data;
        setRows(Array.isArray(r) ? r : (r?.data || []));
        setTotal(r?.totalRowCount || 0);
      })
      .finally(() => setFetching(false));
  }, [status, kind, page, refreshKey]);

  const topRef = useRef(null);
  const [topHeight, setTopHeight] = useState(0);
  useEffect(() => {
    const el = topRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => entries.forEach((e) => setTopHeight(e.contentRect.height)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const decide = useCallback(async (row, verdict) => {
    if (await reviewStatement(row.id, verdict)) setRefreshKey((k) => k + 1);
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'draftKind', label: t('performance.aiCenter.quality.kind'), options: { filter: false, sort: false, customBodyRender: (v) => t(`performance.aiCenter.kind.${v}`, { defaultValue: v }) } },
    { name: 'field', label: t('performance.aiCenter.quality.field'), options: { filter: false, sort: false, customBodyRender: (v) => t(`performance.aiCenter.field.${v}`, { defaultValue: v }) } },
    { name: 'text', label: t('performance.aiCenter.quality.statement'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
    {
      name: 'sources',
      label: t('performance.aiCenter.quality.sources'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => ((v || []).length === 0
          ? t('performance.aiCenter.quality.noSource')
          : (v || []).map((s) => `${t(`performance.aiCenter.sourceType.${s.type}`, { defaultValue: s.type })}${s.label ? `: ${s.label}` : ''}`).join(' · ')),
      },
    },
    { name: 'generatedAt', label: t('performance.aiCenter.quality.generatedAt'), options: { filter: false, sort: false, customBodyRender: (v) => formatDate(v, lang) } },
    { name: 'verdict', label: t('performance.aiCenter.quality.verdict'), options: { filter: false, sort: false, customBodyRender: (v) => (v ? t(`performance.aiCenter.verdict.${v}`) : '-') } },
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          if (!row || row.verdict) return null;
          return (
            <OptionsMenu
              row={row}
              actions={VERDICTS.map((v) => ({ type: v === 'correct' ? 'approve' : v === 'incorrect' ? 'reject' : 'details', label: t(`performance.aiCenter.verdict.${v}`), onClick: () => decide(row, v) }))}
            />
          );
        },
      },
    },
  ], [t, lang, rows, decide]);

  const kinds = quality.data?.byKind || [];
  const tableHeight = containerHeight ? Math.max(containerHeight - topHeight - 160, 200) : undefined;

  return (
    <div style={{ height: '100%', minHeight: 0, overflow: 'hidden' }} data-tab="management">
      <div ref={topRef}>
        <Grid>
          <AiCenterKpis codes={QUALITY_CODES} metrics={quality.data?.metrics} loading={quality.loading} error={quality.error} onRetry={quality.retry} panelPrefix="quality" />
          <PanelCard
            panelKey="quality-kinds"
            span={12}
            compact
            title={t('performance.aiCenter.quality.byKindTitle')}
            loading={quality.loading}
            error={quality.error}
            onRetry={quality.retry}
            empty={kinds.length === 0}
            emptyText={t('performance.aiCenter.quality.empty')}
          >
            <Table>
              <thead>
                <tr>
                  <th>{t('performance.aiCenter.quality.kind')}</th>
                  <th>{t('performance.aiCenter.quality.generated')}</th>
                  <th>{t('performance.aiCenter.quality.verificationFailed')}</th>
                  <th>{t('performance.aiCenter.quality.unavailable')}</th>
                  <th>{t('performance.aiCenter.quality.savedAsIs')}</th>
                  <th>{t('performance.aiCenter.quality.savedEdited')}</th>
                  <th>{t('performance.aiCenter.quality.discarded')}</th>
                  <th>{t('performance.aiCenter.quality.baselineMedian')}</th>
                  <th>{t('performance.aiCenter.quality.aiMedian')}</th>
                </tr>
              </thead>
              <tbody>
                {kinds.map((k) => (
                  <tr key={k.kind} data-kind={k.kind}>
                    <td>{t(`performance.aiCenter.kind.${k.kind}`, { defaultValue: k.kind })}</td>
                    <td>{k.generated}</td>
                    <td>{k.verificationFailed}</td>
                    <td>{k.unavailable}</td>
                    <td>{k.savedAsIs}</td>
                    <td>{k.savedEdited}</td>
                    <td>{k.discarded}</td>
                    <td>{k.baselineMedianSeconds != null ? t('performance.aiCenter.quality.seconds', { value: formatNumber(k.baselineMedianSeconds, lang, 0), n: k.baselineN }) : '-'}</td>
                    <td>{k.aiMedianSeconds != null ? t('performance.aiCenter.quality.seconds', { value: formatNumber(k.aiMedianSeconds, lang, 0), n: k.aiTimedN }) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </PanelCard>
        </Grid>
      </div>
      <div style={{ marginTop: 16 }}>
        <DataTable
          title={t('performance.aiCenter.quality.reviewTitle')}
          fetching={fetching}
          columns={columns}
          data={rows}
          totalRowCount={total}
          pageIndex={page.pageNumber - 1}
          pageSize={page.pageSize}
          disableFilters
          onTableChanged={(action, tableState) => {
            if (action === 'changePage' || action === 'changeRowsPerPage') {
              setPage({ pageNumber: (tableState.page || 0) + 1, pageSize: tableState.rowsPerPage || 10 });
            }
          }}
          toolbarActions={(
            <>
              <div style={{ width: 180 }}>
                <GenericSelect
                  label={t('performance.aiCenter.quality.reviewStatus')}
                  value={status}
                  clearable={false}
                  minimal
                  onChange={(v) => { setStatus(v || 'pending'); setPage((p) => ({ ...p, pageNumber: 1 })); }}
                  options={['pending', 'reviewed'].map((s) => ({ id: s, name: t(`performance.aiCenter.quality.status.${s}`) }))}
                />
              </div>
              <div style={{ width: 200 }}>
                <GenericSelect
                  label={t('performance.aiCenter.quality.kind')}
                  value={kind}
                  minimal
                  onChange={(v) => { setKind(v || ''); setPage((p) => ({ ...p, pageNumber: 1 })); }}
                  options={DRAFT_KINDS.map((k) => ({ id: k, name: t(`performance.aiCenter.kind.${k}`) }))}
                />
              </div>
            </>
          )}
          tableBodyMaxHeight={tableHeight ? `${tableHeight}px` : undefined}
        />
      </div>
    </div>
  );
}
